import { SanitizedHtml } from '../components/SanitizedHtml';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { legalPagesApi, type ImpressumPageContent } from '../services/legalPagesApi';
import { fallbackImpressumData } from '../data/legalPagesData';
import './ImpressumPage.css';

const ImpressumPage: React.FC = () => {
  const location = useLocation();
  const [impressumData, setImpressumData] = useState<ImpressumPageContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Zeitüberschreitung beim Laden der Inhalte.')), 10000)
        );
        
        const apiPromise = legalPagesApi.getImpressumContent();
        
        const content = await Promise.race([apiPromise, timeoutPromise]) as ImpressumPageContent;
        setImpressumData(content);
        
      } catch (err) {
        setImpressumData(fallbackImpressumData);
        console.error('Impressum konnte nicht geladen werden:', err);
        setError('Die Inhalte konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="impressum-loading">
        <div className="container">
          <h2 className="impressum-loading-text">Lade Inhalte...</h2>
        </div>
      </div>
    );
  }

  if (error || !impressumData) {
    return (
      <div className="impressum-loading">
        <div className="container">
          <h2 className="impressum-loading-text">Impressum</h2>
          <p>{error || 'Inhalte können nicht geladen werden'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="impressum-page">
      <div className="container">
        {/* Header */}
        <div className="impressum-header">
          <h1 className="impressum-title">{impressumData.title}</h1>
        </div>

        <div className="impressum-card">
          {/* Contact Information */}
          <div className="impressum-contact-section">
            <h2 className="impressum-section-title">Kontaktinformationen</h2>
            
            <div className="impressum-company-info">
              <h3 className="impressum-company-name">{impressumData.contactInfo.company}</h3>
              <div className="impressum-address">
                {impressumData.contactInfo.address.map((line, index) => (
                  <span key={index} className="impressum-address-line">{line}</span>
                ))}
              </div>
            </div>
            
            <div className="impressum-contact-grid">
              <div className="impressum-contact-item">
                <span className="impressum-contact-label">E-Mail</span>
                <span className="impressum-contact-value">
                  <a href={`mailto:${impressumData.contactInfo.email}`} className="impressum-contact-link">
                    {impressumData.contactInfo.email}
                  </a>
                </span>
              </div>
              
              {impressumData.contactInfo.phone && (
                <div className="impressum-contact-item">
                  <span className="impressum-contact-label">Telefon</span>
                  <span className="impressum-contact-value">
                    <a href={`tel:${impressumData.contactInfo.phone}`} className="impressum-contact-link">
                      {impressumData.contactInfo.phone}
                    </a>
                  </span>
                </div>
              )}
              
              {impressumData.contactInfo.website && (
                <div className="impressum-contact-item">
                  <span className="impressum-contact-label">Website</span>
                  <span className="impressum-contact-value">
                    <a href={impressumData.contactInfo.website} target="_blank" rel="noopener noreferrer" className="impressum-contact-link">
                      {impressumData.contactInfo.website}
                    </a>
                  </span>
                </div>
              )}
              
              {impressumData.contactInfo.taxId && (
                <div className="impressum-contact-item">
                  <span className="impressum-contact-label">Steuernummer</span>
                  <span className="impressum-contact-value">{impressumData.contactInfo.taxId}</span>
                </div>
              )}
              
              {impressumData.contactInfo.commercialRegister && (
                <div className="impressum-contact-item">
                  <span className="impressum-contact-label">Handelsregister</span>
                  <span className="impressum-contact-value">{impressumData.contactInfo.commercialRegister}</span>
                </div>
              )}
              
              {impressumData.contactInfo.representative && (
                <div className="impressum-contact-item">
                  <span className="impressum-contact-label">Vertreten durch</span>
                  <span className="impressum-contact-value">{impressumData.contactInfo.representative}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sections */}
          <div className="impressum-sections">
            {impressumData.sections
              .sort((a, b) => a.order - b.order)
              .map((section, index) => (
              <div key={index} className="impressum-section">
                <h2>{section.title}</h2>
                <SanitizedHtml className="impressum-content" content={section.content} format="markdown" />
              </div>
            ))}
          </div>
        </div>

        {/* Footer Note */}
        <div className="impressum-footer">
          Stand: {new Date(impressumData.generatedAt).toLocaleDateString('de-DE')}
        </div>
      </div>
    </div>
  );
};

export default ImpressumPage;
